import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { USER_GROUP } from '../models/types';
import { authenticateUser } from '../models/users';
import { getApiUrl } from '../utils/apiUrl';

const getDepartmentFromGroups = (groups = []) => {
  const str = Array.isArray(groups) ? groups.join(' ') : String(groups);
  if (/ประปา|น้ำ/i.test(str)) return 'water';
  if (/ไฟฟ้า/i.test(str)) return 'electricity';
  return 'cooperative';
};
const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(() => {
    if (typeof localStorage === 'undefined') return false;
    return Boolean(localStorage.getItem('token'));
  });
  const [helpDeskList, setHelpDeskList] = useState([]);
  const [notifications, setNotifications] = useState([]);

  // เมื่อมี token แต่ไม่มี currentUser (เช่น รีเฟรช) — เรียก GET /me เพื่อตั้ง currentUser ใน state เท่านั้น (ไม่เก็บใน localStorage)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setAuthLoading(false);
      return;
    }
    if (currentUser) {
      setAuthLoading(false);
      return;
    }
    const meUrl = getApiUrl('/api/user/auth/verify/');
    if (!meUrl) {
      setAuthLoading(false);
      return;
    }
    axios
      .get(meUrl, { headers: { Authorization: `Token ${token}` } })
      .then(({ data }) => {
        const raw = data?.user ?? data;
        if (raw) {
          const isStaff = Boolean(raw.is_staff);
          const groups = raw.groups ?? [];
          const userData = {
            id: String(raw.id ?? raw.pk ?? raw.user_id ?? ''),
            username: raw.username ?? '',
            name: raw.name ?? raw.full_name ?? raw.display_name ?? raw.username ?? '',
            group: isStaff ? USER_GROUP.STAFF : USER_GROUP.USER,
            department: getDepartmentFromGroups(groups),
            email: raw.email ?? '',
            groups,
            affiliation_id: raw.affiliation_id ?? null,
            is_staff: isStaff,
            staff_department_ids: Array.isArray(raw.staff_department_ids) ? raw.staff_department_ids : [],
          };
          setCurrentUser(userData);
        }
        setAuthLoading(false);
      })
      .catch(() => {
        setAuthLoading(false);
      });
  }, [currentUser]);

  // Load data from localStorage on mount (ใช้ API แล้ว ไม่มีข้อมูลจำลอง)
  useEffect(() => {
    const savedHelpDeskList = localStorage.getItem('helpDeskList');
    const savedNotifications = localStorage.getItem('notifications');
    if (savedHelpDeskList) {
      try {
        setHelpDeskList(JSON.parse(savedHelpDeskList));
      } catch (_) {}
    }
    if (savedNotifications) {
      try {
        setNotifications(JSON.parse(savedNotifications));
      } catch (_) {}
    }
  }, []);

  // Save HelpDesk list to localStorage whenever they change
  useEffect(() => {
    if (helpDeskList.length > 0 || localStorage.getItem('helpDeskList')) {
      localStorage.setItem('helpDeskList', JSON.stringify(helpDeskList));
    }
  }, [helpDeskList]);

  // Save notifications to localStorage whenever they change
  useEffect(() => {
    if (notifications.length > 0 || localStorage.getItem('notifications')) {
      localStorage.setItem('notifications', JSON.stringify(notifications));
    }
  }, [notifications]);

  // Login with username and password (local/mock)
  const login = (username, password) => {
    const user = authenticateUser(username, password);
    if (user) {
      const { password: _, ...userWithoutPassword } = user;
      setCurrentUser(userWithoutPassword);
      return { success: true };
    }
    return { success: false, error: 'ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง' };
  };

  // Set user from API login response — เก็บแค่ token ใน localStorage
  const setUserFromApi = (data) => {
    const raw = data?.user ?? data;
    if (!raw) return;
    const isStaff = Boolean(raw.is_staff);
    const groups = raw.groups ?? [];
    const user = {
      id: String(raw.id ?? raw.pk ?? raw.user_id ?? ''),
      username: raw.username ?? '',
      name: raw.name ?? raw.full_name ?? raw.display_name ?? raw.username ?? '',
      group: isStaff ? USER_GROUP.STAFF : USER_GROUP.USER,
      department: getDepartmentFromGroups(groups),
      email: raw.email ?? '',
      groups,
      affiliation_id: raw.affiliation_id ?? null,
      is_staff: isStaff,
      staff_department_ids: Array.isArray(raw.staff_department_ids) ? raw.staff_department_ids : [],
    };
    setCurrentUser(user);
    const token = data?.token ?? data?.access;
    if (token) {
      localStorage.setItem('token', token);
    }
  };

  // Logout
  const logout = () => {
    setCurrentUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('selectedAppId');
    localStorage.removeItem('selectedAppStaff');
    localStorage.removeItem('selectedAppName');
    localStorage.removeItem('selectedAppUserHasAccess');
    localStorage.removeItem('staff_department_ids');
  };

  // Create HelpDesk
  const createHelpDesk = (data) => {
    const newItem = {
      id: Date.now().toString(),
      ...data,
      status: 'pending',
      createdAt: new Date().toISOString(),
      history: []
    };
    setHelpDeskList(prev => [...prev, newItem]);
    return newItem;
  };

  // Accept HelpDesk
  const acceptHelpDesk = (itemId, acceptedBy) => {
    setHelpDeskList(prev => prev.map(item => {
      if (item.id === itemId) {
        const historyEntry = {
          action: 'accepted',
          user: acceptedBy.name,
          userType: acceptedBy.type,
          timestamp: new Date().toISOString()
        };
        return {
          ...item,
          status: 'in_progress',
          acceptedBy: acceptedBy.name,
          acceptedAt: new Date().toISOString(),
          history: [...(item.history || []), historyEntry]
        };
      }
      return item;
    }));
  };

  // Complete HelpDesk
  const completeHelpDesk = (itemId, completedBy) => {
    setHelpDeskList(prev => prev.map(item => {
      if (item.id === itemId) {
        const historyEntry = {
          action: 'completed',
          user: completedBy.name,
          userType: completedBy.type,
          timestamp: new Date().toISOString()
        };
        return {
          ...item,
          status: 'completed',
          completedAt: new Date().toISOString(),
          history: [...(item.history || []), historyEntry]
        };
      }
      return item;
    }));
  };

  // Delete HelpDesk (ลบ) – สำหรับผู้แจ้ง ลบได้เฉพาะรายการที่ยังรอรับเรื่อง
  const deleteHelpDesk = (itemId) => {
    setHelpDeskList(prev => prev.filter(t => t.id !== itemId));
  };

  // Add supplementary info (ส่งข้อมูลเพิ่มเติม) – ผู้แจ้งเพิ่มรายละเอียดใน HelpDesk
  const addSupplementaryInfo = (itemId, text, reporterName, images = []) => {
    const hasText = text && String(text).trim();
    const hasImages = images && images.length > 0;
    if (!hasText && !hasImages) return;
    const entry = {
      text: (text && String(text).trim()) || '',
      addedBy: reporterName,
      addedAt: new Date().toISOString(),
      images: Array.isArray(images) ? images : []
    };
    setHelpDeskList(prev => prev.map(item => {
      if (item.id === itemId) {
        const supplementary = [...(item.supplementaryInfo || []), entry];
        const historyEntry = {
          action: 'reporter_supplementary',
          user: reporterName,
          text: entry.text || '(แนบรูป)',
          timestamp: entry.addedAt
        };
        return {
          ...item,
          supplementaryInfo: supplementary,
          history: [...(item.history || []), historyEntry]
        };
      }
      return item;
    }));
  };

  // Create notification
  const createNotification = (notificationData) => {
    const newNotification = {
      id: Date.now().toString(),
      ...notificationData,
      createdAt: new Date().toISOString()
    };
    setNotifications(prev => [...prev, newNotification]);
    return newNotification;
  };

  // Update notification
  const updateNotification = (notificationId, updates) => {
    setNotifications(prev => prev.map(n => {
      if (n.id !== notificationId) return n;
      return {
        ...n,
        ...updates,
        updatedAt: new Date().toISOString(),
      };
    }));
  };

  // Delete notification
  const deleteNotification = (notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  // Get HelpDesk list by user
  const getHelpDeskListByUser = (userId) => {
    return helpDeskList.filter(t => t.reporterId === userId);
  };

  // Get HelpDesk list by category
  const getHelpDeskListByCategory = (category) => {
    return helpDeskList.filter(t => t.category === category);
  };

  // Check SLA violation
  const checkSLAViolation = (item) => {
    if ((item.status || 'pending') !== 'pending') return false;
    
    const createdAt = new Date(item.createdAt);
    const now = new Date();
    const hoursDiff = (now - createdAt) / (1000 * 60 * 60);
    
    const slaHours = 24;
    
    return hoursDiff > slaHours;
  };

  const value = {
    currentUser,
    authLoading,
    helpDeskList,
    notifications,
    login,
    setUserFromApi,
    logout,
    createHelpDesk,
    acceptHelpDesk,
    completeHelpDesk,
    deleteHelpDesk,
    addSupplementaryInfo,
    createNotification,
    updateNotification,
    deleteNotification,
    getHelpDeskListByUser,
    getHelpDeskListByCategory,
    checkSLAViolation,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
